/**
 * Transaction History Component
 *
 * Displays all share transactions with filtering and details.
 * Includes modals for all transaction types including advanced corporate actions.
 */

import { useState, useMemo, useRef, useEffect } from "react";
import {
  ArrowRightLeft,
  Plus,
  Filter,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Clock,
  X,
  User,
  Layers,
  TrendingDown,
  Banknote,
  Lock,
  Divide,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTransactions } from "@/hooks/use-share-book";
import { RegisterTransactionModal } from "./RegisterTransactionModal";
import { BonusIssueModal } from "./BonusIssueModal";
import { SplitModal } from "./SplitModal";
import { ReductionModal } from "./ReductionModal";
import { ConversionModal } from "./ConversionModal";
import { RedemptionModal } from "./RedemptionModal";
import { PledgeModal } from "./PledgeModal";
import type { TransactionType } from "@/lib/api";
import { formatCurrency } from "@/lib/format";

interface TransactionHistoryProps {
  companyId: string;
  shareBook?: {
    hasPreemptionClause: boolean;
    hasConsentClause: boolean;
    hasRedemptionClause: boolean;
    hasConversionClause?: boolean;
  };
  canEdit?: boolean;
  /** Filter to show only transactions for this shareholder */
  filterShareholderId?: string | null;
  /** Name of the filtered shareholder for display */
  filterShareholderName?: string | null;
  /** Callback to clear the shareholder filter */
  onClearShareholderFilter?: () => void;
}

// Transaction action menu items
type TransactionAction =
  | "standard"      // Purchase, gift, inheritance, new_issue
  | "bonus_issue"   // Fondemission
  | "split"         // Split / reverse split
  | "reduction"     // Capital reduction
  | "conversion"    // Share class conversion
  | "redemption"    // Share redemption
  | "pledge";       // Pledge / unpledge

const TRANSACTION_TYPE_OPTIONS: { value: TransactionType | "all"; label: string }[] = [
  { value: "all", label: "Alla typer" },
  { value: "founding", label: "Bildande" },
  { value: "new_issue", label: "Nyemission" },
  { value: "bonus_issue", label: "Fondemission" },
  { value: "purchase", label: "Köp" },
  { value: "gift", label: "Gåva" },
  { value: "inheritance", label: "Arv" },
  { value: "split", label: "Split" },
  { value: "reverse_split", label: "Omvänd split" },
  { value: "reduction", label: "Kapitalreduktion" },
  { value: "conversion", label: "Omvandling" },
  { value: "redemption", label: "Inlösen" },
  { value: "pledge", label: "Pantsättning" },
  { value: "unpledge", label: "Hävd pant" },
  { value: "correction", label: "Rättelse" },
];

export function TransactionHistory({
  companyId,
  shareBook,
  canEdit = true,
  filterShareholderId,
  filterShareholderName,
  onClearShareholderFilter,
}: TransactionHistoryProps) {
  // Modal states
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showBonusIssueModal, setShowBonusIssueModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showReductionModal, setShowReductionModal] = useState(false);
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [showPledgeModal, setShowPledgeModal] = useState(false);

  // Dropdown menu state
  const [showActionMenu, setShowActionMenu] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  const [filterType, setFilterType] = useState<TransactionType | "all">("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Close action menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setShowActionMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle action selection
  const handleActionSelect = (action: TransactionAction) => {
    setShowActionMenu(false);
    switch (action) {
      case "standard":
        setShowRegisterModal(true);
        break;
      case "bonus_issue":
        setShowBonusIssueModal(true);
        break;
      case "split":
        setShowSplitModal(true);
        break;
      case "reduction":
        setShowReductionModal(true);
        break;
      case "conversion":
        setShowConversionModal(true);
        break;
      case "redemption":
        setShowRedemptionModal(true);
        break;
      case "pledge":
        setShowPledgeModal(true);
        break;
    }
  };

  const { data: allTransactions = [], isLoading } = useTransactions(companyId, {
    type: filterType === "all" ? undefined : filterType,
    limit: 100,
  });

  // Filter and sort transactions (newest first)
  const transactions = useMemo(() => {
    let filtered = allTransactions;

    // Filter by shareholder if filter is active
    if (filterShareholderId) {
      filtered = filtered.filter(
        (tx) =>
          tx.fromShareholderId === filterShareholderId ||
          tx.toShareholderId === filterShareholderId
      );
    }

    // Sort by transaction date (newest first), then by registeredAt as secondary sort
    return [...filtered].sort((a, b) => {
      // First compare by transaction date
      const dateA = new Date(a.transactionDate).getTime();
      const dateB = new Date(b.transactionDate).getTime();
      if (dateB !== dateA) return dateB - dateA;

      // If same transaction date, sort by registration time (most recent first)
      const regA = new Date(a.registeredAt).getTime();
      const regB = new Date(b.registeredAt).getTime();
      return regB - regA;
    });
  }, [allTransactions, filterShareholderId]);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getTypeLabel = (type: TransactionType): string => {
    const option = TRANSACTION_TYPE_OPTIONS.find((o) => o.value === type);
    return option?.label || type;
  };

  const getTypeColor = (type: TransactionType): string => {
    switch (type) {
      case "founding":
        return "bg-green-100 text-green-700";
      case "new_issue":
        return "bg-blue-100 text-blue-700";
      case "bonus_issue":
        return "bg-purple-100 text-purple-700";
      case "purchase":
        return "bg-amber-100 text-amber-700";
      case "gift":
        return "bg-pink-100 text-pink-700";
      case "inheritance":
        return "bg-gray-100 text-gray-700";
      case "split":
        return "bg-cyan-100 text-cyan-700";
      case "reverse_split":
        return "bg-indigo-100 text-indigo-700";
      case "reduction":
        return "bg-orange-100 text-orange-700";
      case "conversion":
        return "bg-teal-100 text-teal-700";
      case "redemption":
        return "bg-rose-100 text-rose-700";
      case "pledge":
        return "bg-slate-100 text-slate-700";
      case "unpledge":
        return "bg-emerald-100 text-emerald-700";
      case "correction":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded w-1/3" />
          <div className="h-16 bg-gray-100 rounded" />
          <div className="h-16 bg-gray-100 rounded" />
          <div className="h-16 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <ArrowRightLeft className="text-gray-400" size={24} />
            <h2 className="text-lg font-bold text-gray-900">Transaktioner</h2>
            <span className="text-sm text-gray-500">({transactions.length})</span>
          </div>
          {canEdit && (
            <div className="relative" ref={actionMenuRef}>
              <Button onClick={() => setShowActionMenu(!showActionMenu)}>
                <Plus size={18} className="mr-2" />
                Registrera transaktion
                <ChevronDown size={16} className="ml-2" />
              </Button>

              {/* Action dropdown menu */}
              {showActionMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="py-2">
                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Vanliga transaktioner
                    </div>
                    <button
                      onClick={() => handleActionSelect("standard")}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <ArrowRightLeft size={18} className="text-gray-400" />
                      <div className="text-left">
                        <p className="font-medium">Köp, gåva, arv, emission</p>
                        <p className="text-xs text-gray-500">Standard ägarbyten och emissioner</p>
                      </div>
                    </button>

                    <div className="border-t border-gray-100 my-2" />
                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Bolagsåtgärder
                    </div>

                    <button
                      onClick={() => handleActionSelect("bonus_issue")}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Layers size={18} className="text-purple-500" />
                      <div className="text-left">
                        <p className="font-medium">Fondemission</p>
                        <p className="text-xs text-gray-500">Öka aktiekapital via fritt EK</p>
                      </div>
                    </button>

                    <button
                      onClick={() => handleActionSelect("split")}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Divide size={18} className="text-blue-500" />
                      <div className="text-left">
                        <p className="font-medium">Split / Omvänd split</p>
                        <p className="text-xs text-gray-500">Ändra antal aktier</p>
                      </div>
                    </button>

                    <button
                      onClick={() => handleActionSelect("reduction")}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <TrendingDown size={18} className="text-amber-500" />
                      <div className="text-left">
                        <p className="font-medium">Kapitalreduktion</p>
                        <p className="text-xs text-gray-500">Minska aktiekapitalet</p>
                      </div>
                    </button>

                    <button
                      onClick={() => handleActionSelect("conversion")}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <ArrowRightLeft size={18} className="text-green-500" />
                      <div className="text-left">
                        <p className="font-medium">Omvandling</p>
                        <p className="text-xs text-gray-500">Byt aktieslag</p>
                      </div>
                    </button>

                    <button
                      onClick={() => handleActionSelect("redemption")}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Banknote size={18} className="text-red-500" />
                      <div className="text-left">
                        <p className="font-medium">Inlösen</p>
                        <p className="text-xs text-gray-500">Återköp och makulering</p>
                      </div>
                    </button>

                    <div className="border-t border-gray-100 my-2" />
                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Pantsättning
                    </div>

                    <button
                      onClick={() => handleActionSelect("pledge")}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Lock size={18} className="text-gray-500" />
                      <div className="text-left">
                        <p className="font-medium">Pantsättning / Hävning</p>
                        <p className="text-xs text-gray-500">Registrera eller häv pant</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filter */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as TransactionType | "all")}
              className="input py-2 px-3"
            >
              {TRANSACTION_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Shareholder filter badge */}
          {filterShareholderId && filterShareholderName && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm">
              <User size={14} />
              <span>{filterShareholderName}</span>
              {onClearShareholderFilter && (
                <button
                  onClick={onClearShareholderFilter}
                  className="hover:bg-primary-100 rounded-full p-0.5 transition-colors"
                  title="Rensa filter"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Transaction List */}
        {transactions.length > 0 ? (
          <div className="space-y-3">
            {transactions.map((transaction) => {
              const isExpanded = expandedId === transaction.id;

              return (
                <div
                  key={transaction.id}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Summary Row */}
                  <button
                    onClick={() => toggleExpand(transaction.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xs text-gray-500">
                          {new Date(transaction.transactionDate).toLocaleDateString("sv-SE", {
                            day: "numeric",
                            month: "short",
                          })}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(transaction.transactionDate).getFullYear()}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeColor(transaction.type)}`}>
                            {getTypeLabel(transaction.type)}
                          </span>
                          {transaction.isVerified ? (
                            <CheckCircle size={14} className="text-green-500" />
                          ) : (
                            <Clock size={14} className="text-amber-500" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {transaction.quantity.toLocaleString("sv-SE")} aktier
                          {transaction.totalAmount && (
                            <> • {formatCurrency(parseFloat(transaction.totalAmount))}</>
                          )}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="text-gray-400" size={20} />
                    ) : (
                      <ChevronRight className="text-gray-400" size={20} />
                    )}
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                      <div className="grid sm:grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2 text-sm">
                          <h4 className="font-medium text-gray-700">Detaljer</h4>
                          {transaction.numberFrom && transaction.numberTo && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Aktienummer:</span>
                              <span className="text-gray-900">
                                {transaction.numberFrom}–{transaction.numberTo}
                              </span>
                            </div>
                          )}
                          {transaction.pricePerShare && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Pris per aktie:</span>
                              <span className="text-gray-900">
                                {formatCurrency(parseFloat(transaction.pricePerShare))}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-500">Registrerat:</span>
                            <span className="text-gray-900">
                              {new Date(transaction.registeredAt).toLocaleString("sv-SE")}
                            </span>
                          </div>
                          {transaction.isVerified && transaction.verifiedAt && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Verifierat:</span>
                              <span className="text-gray-900">
                                {new Date(transaction.verifiedAt).toLocaleString("sv-SE")}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="space-y-2 text-sm">
                          {transaction.notes && (
                            <>
                              <h4 className="font-medium text-gray-700">Anteckningar</h4>
                              <p className="text-gray-600">{transaction.notes}</p>
                            </>
                          )}
                          {transaction.isCorrection && (
                            <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                              Detta är en rättelse
                              {transaction.correctionReason && (
                                <>: {transaction.correctionReason}</>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <ArrowRightLeft className="mx-auto text-gray-300 mb-3" size={48} />
            <p className="text-gray-600 mb-4">
              {filterShareholderId
                ? `Inga transaktioner för ${filterShareholderName || "denna aktieägare"}`
                : filterType !== "all"
                  ? "Inga transaktioner av denna typ"
                  : "Inga transaktioner registrerade"}
            </p>
            {filterShareholderId && onClearShareholderFilter ? (
              <Button variant="outline" onClick={onClearShareholderFilter}>
                Visa alla transaktioner
              </Button>
            ) : canEdit && (
              <Button onClick={() => setShowRegisterModal(true)}>
                <Plus size={18} className="mr-2" />
                Registrera transaktion
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Register Transaction Modal (standard transactions) */}
      {showRegisterModal && (
        <RegisterTransactionModal
          companyId={companyId}
          isOpen={showRegisterModal}
          onClose={() => setShowRegisterModal(false)}
          shareBook={shareBook || {
            hasPreemptionClause: false,
            hasConsentClause: false,
            hasRedemptionClause: false,
          }}
        />
      )}

      {/* Bonus Issue Modal (Fondemission) */}
      {showBonusIssueModal && (
        <BonusIssueModal
          isOpen={showBonusIssueModal}
          onClose={() => setShowBonusIssueModal(false)}
          companyId={companyId}
        />
      )}

      {/* Split Modal (Split / Reverse split) */}
      {showSplitModal && (
        <SplitModal
          isOpen={showSplitModal}
          onClose={() => setShowSplitModal(false)}
          companyId={companyId}
        />
      )}

      {/* Reduction Modal (Kapitalreduktion) */}
      {showReductionModal && (
        <ReductionModal
          isOpen={showReductionModal}
          onClose={() => setShowReductionModal(false)}
          companyId={companyId}
        />
      )}

      {/* Conversion Modal (Omvandling mellan aktieslag) */}
      {showConversionModal && (
        <ConversionModal
          isOpen={showConversionModal}
          onClose={() => setShowConversionModal(false)}
          companyId={companyId}
          hasConversionClause={shareBook?.hasConversionClause ?? false}
        />
      )}

      {/* Redemption Modal (Inlösen) */}
      {showRedemptionModal && (
        <RedemptionModal
          isOpen={showRedemptionModal}
          onClose={() => setShowRedemptionModal(false)}
          companyId={companyId}
          hasRedemptionClause={shareBook?.hasRedemptionClause ?? false}
        />
      )}

      {/* Pledge Modal (Pantsättning / Hävning) */}
      {showPledgeModal && (
        <PledgeModal
          isOpen={showPledgeModal}
          onClose={() => setShowPledgeModal(false)}
          companyId={companyId}
        />
      )}
    </>
  );
}
